import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, CheckCircle2, KeyRound } from "lucide-react";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    const res = await resetPassword(token, password);
    setLoading(false);
    if (res.ok) { setDone(true); toast.success(res.message || "Password reset"); }
    else toast.error(res.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="w-full max-w-md border-slate-200 p-8 shadow-lg">
        <div className="mb-6"><Brand /></div>
        {!token ? (
          <div className="text-center">
            <h2 className="font-display text-xl font-bold text-slate-900">Invalid reset link</h2>
            <p className="mt-1 text-sm text-slate-500">This link is missing its token. Please request a new one.</p>
            <Button className="mt-6 w-full bg-slate-900 hover:bg-slate-800" onClick={() => nav("/login")}>Back to sign in</Button>
          </div>
        ) : done ? (
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <h2 className="mt-4 font-display text-xl font-bold text-slate-900">Password reset</h2>
            <p className="mt-1 text-sm text-slate-500">You can now sign in with your new password.</p>
            <Button className="mt-6 w-full gold-cta border-0 font-bold" data-testid="reset-done-btn" onClick={() => nav("/login")}>Go to sign in</Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-slate-900">
              <KeyRound className="h-5 w-5 text-amber-600" />
              <h2 className="font-display text-2xl font-extrabold">Choose a new password</h2>
            </div>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div><Label htmlFor="pw">New password</Label>
                <Input id="pw" type="password" required value={password} data-testid="reset-password"
                  onChange={(e) => setPassword(e.target.value)} className="mt-1.5" placeholder="At least 6 characters" /></div>
              <div><Label htmlFor="cpw">Confirm password</Label>
                <Input id="cpw" type="password" required value={confirm} data-testid="reset-confirm"
                  onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" placeholder="Re-enter password" /></div>
              <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full gold-cta border-0 font-bold h-11">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
